import { Box, Button, Modal } from '@mui/material';
import { ReactComponent as Cross } from 'core/assets/svg/common/cross.svg';
import BaseTypography from 'core/components/typography/BaseTypography';
import { AmplitudeEvents } from 'core/constants/AmplitudeEvents';
import { CurrentPlan } from 'core/constants/enums/CurrentPlan.ts';
import { PricingModalSource } from 'core/constants/enums/PricingModalSource.ts';
import { RendernetErrors } from 'core/constants/enums/RendernetErrors.ts';
import { gtagAnalyticsEvents } from 'core/constants/gtagAnalyticsEvents';
import { metaAnalyticsEvents } from 'core/constants/metaAnalyticsEvents';
import { PostHogEvents } from 'core/constants/PostHogEvents';
import { redditAnalyticsEvents } from 'core/constants/redditAnalyticsEvents';
import { twitterAnalyticsEvents } from 'core/constants/twitterAnalyticsEvents';
import { usePusher } from 'core/hooks/usePusher';
import { pricingModalStyles } from 'core/layouts/components/pricing-modal/PricingModalStyles';
import SubscriptionPlans from 'core/layouts/components/subscription-plans/SubscriptionPlans';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import { saleDataSelector, userProfileSelector } from 'core/store/reducers/appReducer/AppSlice';
import {
	generateErrorSelector,
	openSubscriptionModalSelector,
	toggleSubscriptionModal,
} from 'core/store/reducers/profileReducer/ProfileSlice';
import { captureAmplitudeEvent } from 'core/utils/AmplitudeUtils.ts';
import { gtagAnalyticsUtil } from 'core/utils/gtagAnalyticsUtil';
import { metaAnalyticsUtil } from 'core/utils/metaAnalyticsUtil';
import { capturePosthogEvent } from 'core/utils/PosthogUtil.ts';
import { redditAnalyticsUtil } from 'core/utils/redditAnalyticsUtil';
import { twitterAnalyticsUtil } from 'core/utils/twitterAnalyticsUtil';
import OneTimePurchaseComponent from 'platform/web/one-time-purchase-component/OneTimePurchaseComponent';
import { pricingPageStyles } from 'platform/web/pricing-page/PricingPageStyles';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

function PricingModal() {
	const dispatch = useDispatch();

	usePusher();

	const openPricingModal = useSelector(openSubscriptionModalSelector);
	const handleClose = () =>
		dispatch(
			toggleSubscriptionModal({
				open: false,
				from: PricingModalSource.DEFAULT,
			}),
		);

	const profile = useSelector(userProfileSelector);
	const generateError = useSelector(generateErrorSelector);
	const saleData = useSelector(saleDataSelector);

	useEffect(() => {
		if (openPricingModal?.open) {
			gtagAnalyticsUtil(gtagAnalyticsEvents.CREDITS_OVER);
			twitterAnalyticsUtil(twitterAnalyticsEvents.CREDITS_OVER);
			redditAnalyticsUtil(redditAnalyticsEvents.CREDITS_OVER);
			metaAnalyticsUtil(metaAnalyticsEvents.CREDITS_OVER);
			capturePosthogEvent(PostHogEvents.PRICING_POP_UP_ARRIVE);
			captureAmplitudeEvent(AmplitudeEvents.PRICING_POP_UP_ARRIVE);
		}
	}, [openPricingModal]);

	const title = useMemo(() => {
		if (generateError === RendernetErrors.INSUFFICIENT_FACELOCKS) {
			return 'Keep generating images with Face Lock';
		}
		return saleData?.isLive &&
			profile?.user_id !== '' &&
			profile?.currentPlan?.toLowerCase() === CurrentPlan.FREE ? (
			<span style={{ color: colorPalette.light1 }}>{saleData?.subtitle}</span>
		) : (
			'40% Discount on Yearly Plans!'
		);
	}, [generateError, profile?.currentPlan, profile?.user_id, saleData]);
	const subtitle = useMemo(() => {
		if (generateError === RendernetErrors.INSUFFICIENT_FACELOCKS) {
			return 'Subscribe to a plan or come back tomorrow to get daily free Face Lock generations.';
		}
		return saleData?.isLive &&
			profile?.user_id !== '' &&
			profile?.currentPlan?.toLowerCase() === CurrentPlan.FREE ? (
			<BaseTypography variant="b1_regular" sx={{ color: 'colors.light4' }}>
				{saleData?.couponCode}
			</BaseTypography>
		) : (
			''
		);
	}, [generateError, profile?.currentPlan, profile?.user_id, saleData]);

	const getPostHogEvent = () => {
		if (openPricingModal?.from === PricingModalSource.DO_MORE) {
			return PostHogEvents.VIDEO_ANYONE_SUBSCRIBE_MODAL_CLICK;
		}
		return PostHogEvents.PRICING_POP_UP_ADD_CREDITS_CLICK;
	};

	const getAmplitudeEvent = () => {
		if (openPricingModal?.from === PricingModalSource.DO_MORE) {
			return AmplitudeEvents.VIDEO_ANYONE_SUBSCRIBE_MODAL_CLICK;
		}
		return AmplitudeEvents.PRICING_POP_UP_ADD_CREDITS_CLICK;
	};

	return (
		<Modal
			open={openPricingModal?.open}
			onClose={handleClose}
			data-testid="pricing-modal"
			aria-labelledby="add-credits-modal"
			aria-describedby="Add Credits to continue the generation"
		>
			<Box id="modal-wrapper" sx={pricingModalStyles.modalWrapper}>
				<Box id="modal-body" sx={pricingModalStyles.modalBody}>
					<Button
						id="cross-button"
						onClick={(e) => {
							e.stopPropagation();
							handleClose();
						}}
						sx={pricingModalStyles.crossButton}
					>
						<Cross id="cross-icon" style={pricingModalStyles.cross} />
					</Button>

					<Box sx={[reusableStyles.flexCol, { gap: '6px' }]}>
						{saleData?.isLive &&
						profile?.user_id !== '' &&
						profile?.currentPlan?.toLowerCase() === CurrentPlan.FREE ? (
							<BaseTypography variant="b1_overline_medium" sx={{ color: 'colors.light3' }}>
								{saleData?.title}
							</BaseTypography>
						) : (
							<BaseTypography variant="b1_overline_medium" sx={{ color: 'colors.light3' }}>
								FEBRUARY SALE 🔥
							</BaseTypography>
						)}
						<Box id="pricing-heading" sx={reusableStyles.flexRowWithGap(8)}>
							<BaseTypography
								id="pricing-heading-text"
								key="pricing-heading-text"
								variant="h1_v3_regular"
								styles={{
									color: 'colors.light1',
									fontSize: { xs: '25.5px', sm: '30px', lg: '37.5px' },
								}}
							>
								{saleData?.isLive &&
								profile?.user_id !== '' &&
								profile?.currentPlan?.toLowerCase() === CurrentPlan.FREE ? (
									<span style={{ color: colorPalette.light1 }}>{title}</span>
								) : (
									'40% Discount on Yearly Plans!'
								)}
							</BaseTypography>
						</Box>
						<BaseTypography
							id="pricing-sub-heading"
							key="pricing-sub-heading"
							variant="b1_regular"
							styles={pricingPageStyles.subtitle(
								saleData?.isLive &&
									profile?.user_id !== '' &&
									profile?.currentPlan?.toLowerCase() === CurrentPlan.FREE,
								{ marginBottom: '0px !important' },
							)}
						>
							{saleData?.isLive &&
							profile?.user_id !== '' &&
							profile?.currentPlan?.toLowerCase() === CurrentPlan.FREE ? (
								<Box sx={{ marginBottom: '0px !important', alignSelf: 'center' }}>{subtitle}</Box>
							) : (
								<Box sx={{ marginBottom: '0px !important', alignSelf: 'center' }}>
									<BaseTypography
										variant="b1_regular"
										sx={{ color: 'colors.light4', fontSize: { xs: '12px', sm: '14px' } }}
									>
										Upgrade now! The sale is ending soon ⌛
									</BaseTypography>
								</Box>
							)}
						</BaseTypography>
					</Box>

					{profile?.activePurchase?.subscription ? (
						<SubscriptionPlans
							posthogEvent={getPostHogEvent()}
							amplitudeEvent={getAmplitudeEvent()}
							modal={openPricingModal?.open}
						/>
					) : (
						<OneTimePurchaseComponent
							posthogEvent={getPostHogEvent()}
							amplitudeEvent={getAmplitudeEvent()}
						/>
					)}
				</Box>
			</Box>
		</Modal>
	);
}

export default PricingModal;
